// FramingTool.js
// Reusable pinch-to-zoom / drag-to-pan framing canvas with golden circle overlay.
// Used in CustomerOrderView (customer custom order) and RegisterView (staff direct entry).
//
// Props:
//   imageFile      — File object to frame
//   onCropped      — (blob) => void  — fires on every pan/zoom with the current canvas crop
//   onStateChange  — ({ scale, offset, drawnWidthFrac, drawnHeightFrac, offsetXFrac, offsetYFrac }) => void
//   initialScale   — number, default 1
//   initialOffset  — { x, y }, default { x:0, y:0 }
//   qualityGrade   — optional "great" string to show affirmation + glow
//   size           — optional px size to override the default (Math.min(window.innerWidth - 32, 360))

import { useState, useEffect, useRef } from "react";

const GOLD  = "#fcb31f";
const CREAM = "#ffdaad";

export default function FramingTool({ imageFile, onCropped, initialScale = 1, initialOffset = { x: 0, y: 0 }, initialRotation = 0, onStateChange, qualityGrade, size: sizeProp, rotationEnabled: rotationEnabledProp }) {
  // Rotation is opt-in. Default off so routine zooming can't accidentally
  // tilt the reference. Consumers pass rotationEnabled={true} to show
  // rotation controls and enable the twist gesture.
  const [rotationEnabled, setRotationEnabled] = useState(rotationEnabledProp ?? false);
  const canvasRef = useRef(null);
  const [imgEl, setImgEl] = useState(null);
  const [imgLoading, setImgLoading] = useState(false);
  const [imgError, setImgError]     = useState(null);
  const [offset, setOffset] = useState(initialOffset);
  const [scale, setScale]   = useState(initialScale);
  const [rotation, setRotation] = useState(initialRotation); // degrees, 0 = no rotation
  const [snapEnabled, setSnapEnabled] = useState(true);       // snap to 15° on gesture end
  const lastTouchRef        = useRef(null);
  const lastPinchDistRef    = useRef(null);
  const lastPinchMidRef     = useRef(null);
  const lastPinchAngleRef   = useRef(null);
  // Ref pattern keeps callbacks stable so the canvas effect only fires on actual gesture changes,
  // not every time the parent re-renders (e.g. when analysis results update state).
  const onStateChangeRef = useRef(onStateChange);
  const onCroppedRef     = useRef(onCropped);
  useEffect(() => { onStateChangeRef.current = onStateChange; });
  useEffect(() => { onCroppedRef.current     = onCropped;     });

  const cs = sizeProp ?? Math.min(window.innerWidth - 32, 360);

  useEffect(() => {
    if (!imageFile) return;
    setImgLoading(true);
    setImgError(null);
    setImgEl(null);
    const url = URL.createObjectURL(imageFile);
    const img = new Image();
    img.onload = () => {
      setImgEl(img);
      setImgLoading(false);
      // Default to "cover" scale so the image fills the canvas with no empty space.
      // Only apply when initialScale is the default 1 (fresh photo selection).
      // If initialScale is already set to something specific, we're restoring a saved framing state.
      if (initialScale === 1) {
        const coverScale = Math.max(cs / img.naturalWidth, cs / img.naturalHeight);
        setScale(coverScale);
      }
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      // Most common: HEIC/HEIF from iPhone-sourced photos. Browsers can't
      // decode it natively. Falls through to a user-visible error so the
      // wizard doesn't appear hung indefinitely.
      setImgLoading(false);
      setImgError(imageFile?.name?.match(/\.(heic|heif)$/i)
        ? "This photo is HEIC format and can't be opened in the browser. On iPhone: Settings → Camera → Formats → Most Compatible. On Mac: open the photo in Preview, then File → Export As → JPEG. Then try again."
        : "Couldn't load this image. Try a different photo (JPEG or PNG works best).");
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }, [imageFile]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!imgEl || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext("2d");
    const size   = canvas.width;
    const cx     = size / 2;
    const cy     = size / 2;

    ctx.clearRect(0, 0, size, size);

    const drawW = imgEl.width * scale;
    const drawH = imgEl.height * scale;

    // Rotate around the image center (offset from canvas center by pan), then
    // draw. save/restore isolates the transform so overlay work below is
    // unaffected.
    ctx.save();
    ctx.translate(cx + offset.x, cy + offset.y);
    if (rotation) ctx.rotate((rotation * Math.PI) / 180);
    ctx.drawImage(imgEl, -drawW / 2, -drawH / 2, drawW, drawH);
    ctx.restore();

    // Crop is the full square — SVG overlay handles all visual communication
    canvas.toBlob((blob) => { if (blob) onCroppedRef.current(blob); }, "image/jpeg", 0.92);
  }, [imgEl, offset, scale, rotation]);

  // Persist framing params to parent ref — raw values for UI restore, normalized fracs for Firestore.
  // Guards on imgEl so we don't report until the image has loaded and intrinsic dims are known.
  useEffect(() => {
    if (!imgEl || !canvasRef.current) return;
    const canvasSize = canvasRef.current.width;
    onStateChangeRef.current?.({
      scale,
      offset,
      rotation,
      drawnWidthFrac:  (imgEl.width  * scale) / canvasSize,
      drawnHeightFrac: (imgEl.height * scale) / canvasSize,
      offsetXFrac:     offset.x / canvasSize,
      offsetYFrac:     offset.y / canvasSize,
    });
  }, [scale, offset, rotation, imgEl]);

  function handleTouchStart(e) {
    if (e.touches.length === 1) {
      lastTouchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    } else if (e.touches.length === 2) {
      lastPinchDistRef.current  = getPinchDistance(e.touches);
      lastPinchMidRef.current   = { x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
                                     y: (e.touches[0].clientY + e.touches[1].clientY) / 2 };
      lastPinchAngleRef.current = getPinchAngle(e.touches);
    }
  }

  function handleTouchMove(e) {
    e.preventDefault();
    if (e.touches.length === 1 && lastTouchRef.current) {
      const dx = e.touches[0].clientX - lastTouchRef.current.x;
      const dy = e.touches[0].clientY - lastTouchRef.current.y;
      setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      lastTouchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    } else if (e.touches.length === 2 && lastPinchDistRef.current) {
      const newDist  = getPinchDistance(e.touches);
      const newMid   = { x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
                         y: (e.touches[0].clientY + e.touches[1].clientY) / 2 };
      const newAngle = getPinchAngle(e.touches);
      const ratio    = newDist / lastPinchDistRef.current;
      // Minimum scale: image's shorter dimension must fill ≥60% of canvas.
      const minScale = imgEl ? (cs * 0.6) / Math.min(imgEl.naturalWidth || imgEl.width, imgEl.naturalHeight || imgEl.height) : 0.05;
      setScale(prev => Math.max(minScale, Math.min(5, prev * ratio)));
      if (lastPinchMidRef.current) {
        const dx = newMid.x - lastPinchMidRef.current.x;
        const dy = newMid.y - lastPinchMidRef.current.y;
        setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      }
      if (lastPinchAngleRef.current != null && rotationEnabled) {
        // Shortest-arc delta so wrapping past ±180° doesn't jump.
        // Positive delta = image rotates same direction as fingers.
        let dAng = newAngle - lastPinchAngleRef.current;
        if (dAng >  180) dAng -= 360;
        if (dAng < -180) dAng += 360;
        setRotation(prev => normalizeAngle(prev + dAng));
      }
      lastPinchDistRef.current  = newDist;
      lastPinchMidRef.current   = newMid;
      lastPinchAngleRef.current = newAngle;
    }
  }

  function handleTouchEnd() {
    // Snap to 15° increments on release when rotation + snap are both on.
    if (rotationEnabled && snapEnabled && lastPinchAngleRef.current != null) {
      setRotation(prev => snapToIncrement(prev, 15));
    }
    lastTouchRef.current      = null;
    lastPinchDistRef.current  = null;
    lastPinchMidRef.current   = null;
    lastPinchAngleRef.current = null;
  }

  // ── Mouse handlers (desktop drag + scroll-wheel zoom) ──────────────────
  const mouseDownRef = useRef(false);
  const lastMouseRef = useRef(null);

  function handleMouseDown(e) {
    e.preventDefault();
    mouseDownRef.current = true;
    lastMouseRef.current = { x: e.clientX, y: e.clientY };
  }

  function handleMouseMove(e) {
    if (!mouseDownRef.current || !lastMouseRef.current) return;
    e.preventDefault();
    const dx = e.clientX - lastMouseRef.current.x;
    const dy = e.clientY - lastMouseRef.current.y;
    setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
    lastMouseRef.current = { x: e.clientX, y: e.clientY };
  }

  function handleMouseUp() {
    mouseDownRef.current = false;
    lastMouseRef.current = null;
  }

  function handleWheel(e) {
    e.preventDefault();
    const minScale = imgEl ? (cs * 0.6) / Math.min(imgEl.naturalWidth || imgEl.width, imgEl.naturalHeight || imgEl.height) : 0.05;
    const delta = e.deltaY > 0 ? 0.92 : 1.08; // scroll down = zoom out, up = zoom in
    setScale(prev => Math.max(minScale, Math.min(5, prev * delta)));
  }

  function getPinchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }
  function getPinchAngle(touches) {
    const dx = touches[1].clientX - touches[0].clientX;
    const dy = touches[1].clientY - touches[0].clientY;
    return (Math.atan2(dy, dx) * 180) / Math.PI;
  }
  function normalizeAngle(deg) {
    // Keep rotation in (-180, 180] so the value stays readable.
    let d = deg % 360;
    if (d > 180) d -= 360;
    if (d <= -180) d += 360;
    return d;
  }
  function snapToIncrement(deg, step) {
    return normalizeAngle(Math.round(deg / step) * step);
  }

  function resetFraming() {
    setRotation(0);
    setOffset({ x: 0, y: 0 });
    if (imgEl) {
      const coverScale = Math.max(cs / imgEl.naturalWidth, cs / imgEl.naturalHeight);
      setScale(coverScale);
    } else {
      setScale(1);
    }
  }

  const r  = cs / 2 - 6; // circle radius — slight inset from edge
  const cx = cs / 2;
  const cy = cs / 2;

  // Even-odd path: rect minus circle = outside-circle region for dimming
  const circleD   = `M ${cx} ${cy - r} A ${r} ${r} 0 1 0 ${cx} ${cy + r} A ${r} ${r} 0 1 0 ${cx} ${cy - r} Z`;
  const cornerPath = `M 0 0 H ${cs} V ${cs} H 0 Z ${circleD}`;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div style={{ position: "relative", width: cs, height: cs, borderRadius: "8px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,0.6)" }}>
        <canvas
          ref={canvasRef}
          width={cs}
          height={cs}
          style={{ display: "block", touchAction: "none", cursor: mouseDownRef.current ? "grabbing" : "grab" }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        />
        {/* ── Photo load error — shown when the browser couldn't decode the file (HEIC, corrupt, etc) ── */}
        {imgError && (
          <div style={{ position: "absolute", inset: 0, background: "#1a0f14", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "0.8rem", padding: "1.25rem", textAlign: "center" }}>
            <span style={{ fontSize: "2rem" }}>⚠️</span>
            <p style={{ color: "#ef537e", fontSize: "0.84rem", margin: 0, fontWeight: "bold" }}>Couldn't load the photo</p>
            <p style={{ color: "#ffdaad", fontSize: "0.76rem", margin: 0, lineHeight: 1.45 }}>{imgError}</p>
          </div>
        )}
        {/* ── Photo loading overlay — shown while browser decodes the image ── */}
        {imgLoading && (
          <div style={{ position: "absolute", inset: 0, background: "#091520", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "1rem" }}>
            <style>{`
              @keyframes gts-pancake-bounce {
                0%, 100% { transform: translateY(0) rotate(-4deg); }
                50%       { transform: translateY(-10px) rotate(4deg); }
              }
              @keyframes gts-photo-shimmer {
                0%   { transform: translateX(-100%); }
                100% { transform: translateX(200%); }
              }
            `}</style>
            <span style={{ fontSize: "2.5rem", animation: "gts-pancake-bounce 1.2s ease-in-out infinite", display: "block" }}>🥞</span>
            <div style={{ textAlign: "center" }}>
              <p style={{ color: GOLD, fontSize: "0.85rem", margin: "0 0 0.6rem", fontWeight: "bold" }}>Loading your photo...</p>
              <div style={{ width: "120px", height: "3px", background: "#152a40", borderRadius: "2px", overflow: "hidden", position: "relative" }}>
                <div style={{ position: "absolute", inset: 0, background: `linear-gradient(90deg, transparent 0%, ${GOLD} 50%, transparent 100%)`, animation: "gts-photo-shimmer 1.4s ease-in-out infinite" }} />
              </div>
            </div>
          </div>
        )}
        <svg
          width={cs}
          height={cs}
          viewBox={`0 0 ${cs} ${cs}`}
          style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
        >
          <defs>
            <filter id="circle-glow" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Corner dimming — fills region outside the circle */}
          <path fillRule="evenodd" d={cornerPath} fill="rgba(0,0,0,0.32)" />

          {/* Circle outline — gold normally, bright warm-white glow when quality is great */}
          <circle
            cx={cx} cy={cy} r={r} fill="none"
            stroke={qualityGrade === "great" ? "rgba(255,235,120,0.95)" : "rgba(252,179,31,0.7)"}
            strokeWidth={qualityGrade === "great" ? 2.5 : 2}
            filter="url(#circle-glow)"
          >
            {qualityGrade === "great" && (
              <animate attributeName="opacity" values="0.85;1;0.85" dur="2s" repeatCount="indefinite" />
            )}
          </circle>

          {/* "SAFE ZONE" label — inside circle near top */}
          <text x={cx} y={cy - r + 20} textAnchor="middle" fill="rgba(252,179,31,0.72)" fontSize="10" fontWeight="700" letterSpacing="2" fontFamily="sans-serif">SAFE ZONE</text>

          {/* Corner labels — two diagonal corners, very subtle */}
          <text x={8} y={15} fill="rgba(255,255,255,0.22)" fontSize="8" fontFamily="sans-serif">no guarantees</text>
          <text x={cs - 8} y={cs - 7} textAnchor="end" fill="rgba(255,255,255,0.22)" fontSize="8" fontFamily="sans-serif">no guarantees</text>

        </svg>
      </div>
      {qualityGrade === "great" ? (
        <p style={{ color: "#ffe878", fontSize: "0.85rem", marginTop: "0.75rem", textAlign: "center", lineHeight: 1.5, fontWeight: "bold" }}>
          Great framing — Dan's going to love working with this ✓
        </p>
      ) : (
        <p style={{ color: CREAM, fontSize: "0.85rem", marginTop: "0.75rem", textAlign: "center", lineHeight: 1.5 }}>
          Fill the circle with your subject — the bigger, the better the pancake!
        </p>
      )}
      <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem", marginTop: "0.25rem", textAlign: "center" }}>
        Drag to reposition · Pinch to zoom{rotationEnabled ? " · Twist to rotate" : ""}
      </p>
      {/* Rotation toggle — tucked below the instructions, lets guest opt in */}
      {imgEl && !rotationEnabled && (
        <button
          type="button"
          onClick={() => setRotationEnabled(true)}
          style={{
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.14)",
            color: "rgba(255,255,255,0.4)",
            borderRadius: "999px",
            padding: "0.3rem 0.75rem",
            fontSize: "0.72rem",
            fontWeight: 600,
            cursor: "pointer",
            marginTop: "0.4rem",
          }}
        >
          🔄 Enable rotation
        </button>
      )}
      {/* Rotation toolbar — shown when rotation is enabled */}
      {imgEl && rotationEnabled && (
        <div style={{ display: "flex", gap: "0.45rem", marginTop: "0.6rem", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
          {[-90, -15, +15, +90].map((step) => (
            <button
              key={step}
              type="button"
              onClick={() => {
                setRotation((prev) => {
                  let next = prev + step;
                  next = ((next + 180) % 360 + 360) % 360 - 180;
                  return next;
                });
              }}
              style={{
                background:   "rgba(252,179,31,0.1)",
                border:       "1px solid rgba(252,179,31,0.35)",
                color:        GOLD,
                borderRadius: "999px",
                padding:      "0.38rem 0.75rem",
                fontSize:     "0.78rem",
                fontWeight:   700,
                minWidth:     "3rem",
                cursor:       "pointer",
              }}
              aria-label={`Rotate ${step > 0 ? "+" : ""}${step} degrees`}
            >
              {step > 0 ? `+${step}°` : `${step}°`}
            </button>
          ))}
          <span style={{
            color: rotation !== 0 ? GOLD : "rgba(255,255,255,0.35)",
            fontSize: "0.78rem",
            fontWeight: 700,
            fontVariantNumeric: "tabular-nums",
            minWidth: "3ch",
            textAlign: "center",
          }}>
            {Math.round(rotation)}°
          </span>
          {(rotation !== 0 || scale !== 1 || offset.x !== 0 || offset.y !== 0) && (
            <button
              type="button"
              onClick={resetFraming}
              style={{
                background:   "rgba(255,255,255,0.06)",
                border:       "1px solid rgba(255,255,255,0.2)",
                color:        "rgba(255,255,255,0.7)",
                borderRadius: "999px",
                padding:      "0.38rem 0.75rem",
                fontSize:     "0.78rem",
                fontWeight:   700,
                cursor:       "pointer",
              }}
              aria-label="Reset framing to original"
            >
              🔄 Reset
            </button>
          )}
          <button
            type="button"
            onClick={() => setSnapEnabled(v => !v)}
            title={snapEnabled ? "Snap to 15° — tap for free rotate" : "Free rotate — tap to snap"}
            aria-label="Toggle twist snap"
            style={{
              background:   "transparent",
              border:       "1px solid rgba(255,255,255,0.14)",
              color:        snapEnabled ? GOLD : "rgba(255,255,255,0.4)",
              borderRadius: "999px",
              width:        "2rem",
              height:       "2rem",
              padding:      0,
              fontSize:     "0.8rem",
              cursor:       "pointer",
            }}
          >
            🧲
          </button>
          <button
            type="button"
            onClick={() => { setRotation(0); setRotationEnabled(false); }}
            title="Disable rotation"
            style={{
              background:   "transparent",
              border:       "1px solid rgba(255,255,255,0.14)",
              color:        "rgba(255,255,255,0.4)",
              borderRadius: "999px",
              padding:      "0.3rem 0.7rem",
              fontSize:     "0.72rem",
              fontWeight:   600,
              cursor:       "pointer",
            }}
          >
            ✕ Rotation
          </button>
        </div>
      )}
    </div>
  );
}
